package fr.frux.core.designsystem.theme

import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.Immutable
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.graphics.Color

@Immutable
data class FruxExtendedColors(
    val accent: Color,
    val onAccent: Color,
    // Accent « feuille » (#198) : #4C9A2A, ~3,5:1 sur blanc — c'est PRÉCISÉMENT la correction de
    // l'issue #198 (le vert forêt primaire, lui, tient l'AA texte). N'utiliser accentLeaf QUE
    // comme surface/accent secondaire (vignettes espèce, fonds d'illustration) ou comme teinte
    // d'icône/élément graphique (seuil 3:1) — jamais en couleur de texte sur fond clair, jamais en
    // fond de texte blanc. onAccentLeaf (texte sombre) tient l'AA texte (4,5:1) posé sur accentLeaf
    // ou sur accentLeafContainer ; voir ContrastTest pour la vérification outillée.
    val accentLeaf: Color,
    val onAccentLeaf: Color,
    // Fond clair pour les vignettes espèce (icône teintée accentLeaf dessus, cf. TreeCard.glyph).
    val accentLeafContainer: Color,
    // Accent « cerise » (#198) : cœur des favoris (état actif). Icône/élément graphique
    // uniquement (seuil 3:1 sur surface) ; onAccentCherry tient l'AA texte si jamais utilisé en
    // fond plein avec du texte/une icône dessus.
    val accentCherry: Color,
    val onAccentCherry: Color,
    val statusAvailable: Color,
    val onStatusAvailable: Color,
    val statusFlowering: Color,
    val onStatusFlowering: Color,
    val statusPicked: Color,
    val onStatusPicked: Color,
    val statusUnavailable: Color,
    val onStatusUnavailable: Color,
    // Absence d'observation, pas un état : un gris neutre, qui n'affirme ni disponibilité ni refus.
    val statusUnknown: Color,
    val onStatusUnknown: Color,
    // Palette des marqueurs de carte. Teintes sombres et saturées, choisies pour contraster avec
    // les beiges et verts clairs d'un fond de carte. `markerFeuille` n'est délibérément pas un
    // vert franc : ce serait la teinte du fond dans un parc, là où poussent la plupart des arbres.
    val markerFruit: Color,
    val markerFleur: Color,
    val markerFeuille: Color,
    val markerNoix: Color,
    val markerRacine: Color,
    // Statut non actionnable (cueilli, inaccessible) : écrase la teinte de partie.
    val markerInactive: Color,
    // Palette « hybride famille × phase » des marqueurs de carte (#204, amende #196) : la plaque
    // encode desormais la famille de fruit (glyphe = espece, phase = traitement plein/pale/gris —
    // cf. TreeMapper.speciesFamilyColor/hybridMarkerTint). Seuil retenu : 3:1 (elements
    // graphiques, WCAG 2.1 1.4.11) contre blanc en clair (fond de carte approxime) et contre la
    // surface sombre en sombre — verifie par ContrastTest.
    val familyPomme: Color,
    val familyRouge: Color,
    val familyDore: Color,
    val familyPrune: Color,
    val familyCoque: Color,
    val familyAutre: Color,
    // Plaque d'un lieu de cueillette verrouille : un gris chaud qui se lit comme « ferme » sans
    // emprunter le gris des arbres inactifs.
    val siteLocked: Color,
)

internal val LightExtendedColors =
    FruxExtendedColors(
        accent = Terracotta,
        onAccent = Color.White,
        accentLeaf = Color(0xFF4C9A2A),
        onAccentLeaf = Color(0xFF0F1F0D),
        accentLeafContainer = Color(0xFFE8F3DF),
        accentCherry = Color(0xFFC6314B),
        onAccentCherry = Color.White,
        statusAvailable = Color(0xFF4C8C57),
        onStatusAvailable = Color.White,
        statusFlowering = Color(0xFFD9972F),
        onStatusFlowering = Color(0xFF3D2A05),
        statusPicked = Color(0xFF69695E),
        onStatusPicked = Color.White,
        statusUnavailable = Color(0xFFC05B4D),
        onStatusUnavailable = Color.White,
        statusUnknown = Color(0xFF67675F),
        onStatusUnknown = Color.White,
        markerFruit = Color(0xFFC4733D),
        markerFleur = Color(0xFFA83E6E),
        markerFeuille = Color(0xFF17786B),
        markerNoix = Color(0xFF6B4423),
        markerRacine = Color(0xFF7A5AA0),
        markerInactive = Color(0xFF8E8E85),
        familyPomme = Color(0xFF4C9A2A), // = accentLeaf, ~3,49:1 sur blanc
        familyRouge = Color(0xFFC6314B), // = accentCherry, ~5,32:1 sur blanc
        // Assombri depuis #D98E24 (valeur mock/anneau "bientot mur") : le doré tel quel n'atteint que
        // ~2,68:1 sur blanc, sous le seuil graphique. #B8791F tient ~3,62:1. La teinte de l'anneau
        // ambre (core-maps, cf. #204) reste #D98E24 — deux usages independants, pas de couplage.
        familyDore = Color(0xFFB8791F),
        familyPrune = Color(0xFF7A4E9E), // ~6,15:1 sur blanc
        familyCoque = Color(0xFF7B5233), // ~6,78:1 sur blanc
        familyAutre = Color(0xFF3E7D74), // ~4,78:1 sur blanc
        siteLocked = Color(0xFF8A877B),
    )

internal val DarkExtendedColors =
    FruxExtendedColors(
        accent = Color(0xFFE0A46B),
        onAccent = Color(0xFF3E2510),
        accentLeaf = Color(0xFF7ED659),
        onAccentLeaf = Color(0xFF0F1F0D),
        accentLeafContainer = Color(0xFF223821),
        accentCherry = Color(0xFFE8879A),
        onAccentCherry = Color(0xFF300712),
        statusAvailable = Color(0xFF8FC79A),
        onStatusAvailable = Color(0xFF0F2915),
        statusFlowering = Color(0xFFE9BC6E),
        onStatusFlowering = Color(0xFF3D2A05),
        statusPicked = Color(0xFFB3B3A8),
        onStatusPicked = Color(0xFF26261F),
        statusUnavailable = Color(0xFFDD9284),
        onStatusUnavailable = Color(0xFF44150C),
        statusUnknown = Color(0xFFC9C9BF),
        onStatusUnknown = Color(0xFF1B1B17),
        markerFruit = Color(0xFFC4733D),
        markerFleur = Color(0xFFA83E6E),
        markerFeuille = Color(0xFF17786B),
        markerNoix = Color(0xFF6B4423),
        markerRacine = Color(0xFF7A5AA0),
        markerInactive = Color(0xFF8E8E85),
        // Variantes claires/saturees, memes teintes que le theme clair, coherentes avec les autres
        // paires accent*/accent*Dark de ce fichier (ex. accentLeaf -> #7ED659). Toutes tiennent tres
        // largement le seuil 3:1 contre la surface sombre (#212822, ratios ~5,8 a ~8,4:1).
        familyPomme = Color(0xFF7ED659), // = accentLeaf (sombre)
        familyRouge = Color(0xFFE8879A), // = accentCherry (sombre)
        familyDore = Color(0xFFE9BC6E), // = statusFlowering (sombre)
        familyPrune = Color(0xFFB79AD9),
        familyCoque = Color(0xFFC79868),
        familyAutre = Color(0xFF6FB5AB),
        siteLocked = Color(0xFF9A978B),
    )

internal val LocalFruxColors = staticCompositionLocalOf { LightExtendedColors }

val MaterialTheme.fruxColors: FruxExtendedColors
    @Composable get() = LocalFruxColors.current

/** Statut d'affichage d'un arbre, découplé du domaine (cf. issue #28). */
enum class FruxStatus { Available, Flowering, Picked, OffSeason, Unavailable, Unknown }

val FruxStatus.label: String
    get() =
        when (this) {
            FruxStatus.Available -> "Prêt à cueillir"
            FruxStatus.Flowering -> "Bientôt mûr"
            FruxStatus.Picked -> "Cueilli"
            FruxStatus.OffSeason -> "Hors saison"
            FruxStatus.Unavailable -> "Inaccessible"
            FruxStatus.Unknown -> "État inconnu"
        }

@Composable
fun FruxStatus.color(): Color =
    when (this) {
        FruxStatus.Available -> MaterialTheme.fruxColors.statusAvailable

        FruxStatus.Flowering -> MaterialTheme.fruxColors.statusFlowering

        FruxStatus.Picked -> MaterialTheme.fruxColors.statusPicked

        // Hors saison est un état neutre dérivé du référentiel (calendrier d'espèce), pas un
        // signalement utilisateur : pas de token dédié tant que le restyling #198 n'est pas passé.
        FruxStatus.OffSeason -> MaterialTheme.fruxColors.statusUnknown

        FruxStatus.Unavailable -> MaterialTheme.fruxColors.statusUnavailable

        FruxStatus.Unknown -> MaterialTheme.fruxColors.statusUnknown
    }

@Composable
fun FruxStatus.onColor(): Color =
    when (this) {
        FruxStatus.Available -> MaterialTheme.fruxColors.onStatusAvailable

        FruxStatus.Flowering -> MaterialTheme.fruxColors.onStatusFlowering

        FruxStatus.Picked -> MaterialTheme.fruxColors.onStatusPicked

        // cf. color() ci-dessus : même token neutre que Unknown.
        FruxStatus.OffSeason -> MaterialTheme.fruxColors.onStatusUnknown

        FruxStatus.Unavailable -> MaterialTheme.fruxColors.onStatusUnavailable

        FruxStatus.Unknown -> MaterialTheme.fruxColors.onStatusUnknown
    }
